import { useRef } from 'react';
import { useAtomValue } from 'jotai';
import { cn } from '@rocco/utils/cn';
import {
    _tileMediaVariants,
    TileMedia,
} from '@rocco/ui/tile-shared/components/TileMedia';
import type { TileViewModel } from '@rocco/ui/tile-shared/view-models/tile';
import type { ProfessionalTileViewModel } from '@rocco/ui/tile-professional/view-models/professional';
import { TileFollowButton } from '@rocco/ui/tile-shared/components/TileFollowButton';
import { ProfessionalTileLogo } from './ProfessionalTileLogo';
import { ProfessionalTileServices } from './ProfessionalTileServices';
import { uiContextAtomTileActions } from '@rocco/ui/tile-shared/hooks/use-shared-tiles';
import { useTracker } from '../../../../../tracker';
import { trackTileMediaDetailClick } from '@archipro-website/web/app/modules/tracking/util/trackTileMedia';
import { OptionalLink } from '@rocco/components/link/OptionalLink';
import { TILE_COMPONENTS_CLASS_NAMES } from '@rocco/ui/tile-shared/utils/contstants';

export const ProfessionalTile = (
    props: TileViewModel<ProfessionalTileViewModel> & {
        hideServiceTags?: boolean;
        rounded?: boolean;
    },
) => {
    const {
        aspectRatio,
        model: professional,
        section,
        enableTileSlider,
        isFollowed,
        onFollow,
        hideFollowButton = false,
        followButton,
        overlay,
        compact = false,
        hideServiceTags = false,
        // rounded,
        inlineActionButtons,
        disableLink = false,
        onClick,
        className,
        contentEditable,
        eager,
    } = props as TileViewModel<ProfessionalTileViewModel> & {
        hideCategory?: boolean;
        rounded?: boolean;
    };

    const tileActions = useAtomValue(uiContextAtomTileActions);
    const tileRef = useRef<HTMLAnchorElement>(null);
    const isSponsored = professional.services.includes('Sponsored');
    const tracker = useTracker();

    const handleTileClick = () => {
        onClick?.();
        if (disableLink) {
            return;
        }
        tileActions?.trackingConfig?.onClick?.(
            tileRef,
            professional.id,
            professional.id,
            section,
        );

        trackTileMediaDetailClick(tracker, {
            itemID: professional.id,
            itemType: 'ProfessionalTile',
            itemName: professional.title,
        });
    };

    return (
        <OptionalLink
            ref={tileRef}
            mode={disableLink ? 'override' : 'normal'}
            prefetch="intent"
            to={professional.link}
            onClick={handleTileClick}
        >
            <div className={cn('flex flex-col gap-4', className)}>
                <div
                    className={cn(
                        'group relative',
                        _tileMediaVariants({ aspectRatio }),
                    )}
                >
                    <TileMedia
                        aspectRatio={aspectRatio}
                        images={[professional.image]}
                        video={professional.video}
                        enableTileSlider={enableTileSlider}
                        eager={eager}
                        onSlideChange={(direction) => {
                            tileActions?.trackingConfig?.onTileMediaSliderSlideChange?.(
                                direction,
                                professional.id,
                            );
                        }}
                    />

                    {overlay ? (
                        overlay
                    ) : (
                        <>
                            <div className="absolute inset-0 hidden rounded-tile bg-gray-900/10 opacity-0 transition-opacity duration-300 md:block md:group-hover:opacity-100" />
                            <div className="absolute right-4 top-3.5 flex gap-1 transition-opacity duration-300 md:invisible md:opacity-0 md:group-hover:visible md:group-hover:opacity-100">
                                {followButton
                                    ? followButton
                                    : !hideFollowButton && (
                                          <TileFollowButton
                                              pinId={professional.id}
                                              pinType="ProfessionalPin"
                                              isFollowed={isFollowed}
                                              onFollow={onFollow}
                                          />
                                      )}
                            </div>
                        </>
                    )}

                    {professional.logo && (
                        <div className="absolute left-2.5 top-2 md:left-4 md:top-4">
                            <ProfessionalTileLogo
                                image={professional.logo.image}
                                background={professional.logo.background}
                                compact={compact}
                            />
                        </div>
                    )}
                    {isSponsored && (
                        <div className="text-md absolute right-0 mt-3 text-gray-900/40">
                            <span>Sponsored</span>
                        </div>
                    )}
                </div>

                <div className="flex flex-col gap-4">
                    {inlineActionButtons && (
                        <div className="flex flex-col">
                            {inlineActionButtons}
                        </div>
                    )}
                    <div
                        className={cn(
                            TILE_COMPONENTS_CLASS_NAMES.TILE_DESCRIPTION_BLOCK,
                            'flex flex-col md:gap-0.5',
                        )}
                    >
                        <span
                            contentEditable={contentEditable?.enabled.title}
                            suppressContentEditableWarning={true}
                            onInput={(e: React.FormEvent<HTMLSpanElement>) =>
                                contentEditable?.onChange?.(
                                    (e.target as HTMLSpanElement).textContent ??
                                        '',
                                    'title',
                                )
                            }
                            onBlur={() => {
                                contentEditable?.onBlur?.();
                            }}
                            onKeyDown={(
                                e: React.KeyboardEvent<HTMLSpanElement>,
                            ) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    (e.target as HTMLSpanElement).blur();
                                }
                            }}
                            className={cn(
                                'overflow-hidden text-ellipsis text-nowrap text-sm font-medium tracking-[0.03rem] md:h-[1.4rem] md:text-base md:tracking-[0.02rem]',
                                contentEditable?.enabled.title &&
                                    'focus-within:border-primary-500 -mx-2 cursor-text rounded-md border border-transparent px-2 focus-within:bg-gray-100 focus-within:text-gray-900 hover:border-gray-500',
                            )}
                        >
                            {professional.title}
                        </span>
                        <span
                            contentEditable={
                                contentEditable?.enabled.description
                            }
                            suppressContentEditableWarning={true}
                            onInput={(e: React.FormEvent<HTMLSpanElement>) =>
                                contentEditable?.onChange?.(
                                    (e.target as HTMLSpanElement).textContent ??
                                        '',
                                    'description',
                                )
                            }
                            onBlur={() => {
                                contentEditable?.onBlur?.();
                            }}
                            onKeyDown={(
                                e: React.KeyboardEvent<HTMLSpanElement>,
                            ) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    (e.target as HTMLSpanElement).blur();
                                }
                            }}
                            className={cn(
                                'overflow-hidden text-ellipsis text-nowrap text-sm font-medium tracking-[0.03rem] text-gray-900/40 md:h-[1.4rem] md:text-base md:tracking-[0.02rem]',
                                contentEditable?.enabled.description &&
                                    'focus-within:border-primary-500 -ml-2 cursor-text rounded-md border border-transparent px-2 focus-within:bg-gray-100 focus-within:text-gray-900 hover:border-gray-500',
                            )}
                        >
                            {professional.category}
                        </span>
                        {professional.location && (
                            <span className="overflow-hidden text-ellipsis text-nowrap text-sm tracking-[0.03rem] text-gray-900/40 md:text-base md:tracking-[0.02rem]">
                                {professional.location}
                            </span>
                        )}
                    </div>

                    {!hideServiceTags && (
                        <>
                            <div className="hidden justify-start gap-2 md:flex">
                                <ProfessionalTileServices
                                    services={professional.services}
                                />
                            </div>
                            <div className="flex justify-start gap-2 md:hidden">
                                <ProfessionalTileServices
                                    limit={1}
                                    services={professional.services}
                                />
                            </div>
                        </>
                    )}
                </div>
            </div>
        </OptionalLink>
    );
};
